import Icon from "../Icon";
import Button from "../Button";
import React, {useContext, useEffect, useState} from "react";
import {SelectedWishlistContext} from "../../contexts/SelectedWishlistContext";
import {useMutation, useQueryClient} from '@tanstack/react-query';
import {Spinner} from "react-bootstrap";
import {useUserLoggedIn} from "../../hooks/useUserLoggedIn";
import {useCountryLang} from "../../hooks/useCountryLang";
import {LoggedOutWishlistContext} from "../../contexts/LoggedOutWishlistContext";
import {getLocalStorageItem, setLocalStorageItem} from "../../shared/localStorageUtil";
import {ILoggedOutShoppingList} from "../../interfaces/ILoggedOutShoppingList";
import {request} from "../../shared/httpInterceptor";
import {toast} from "react-toastify";
import {useAtom} from "jotai";
import {nextSessionAtom} from "../../store/sessionAtom";

type AddToWishlistPropsType = {
  productId: number,
  wishlistId?: number,
  width?: string,
  variant?: "primary" | "secondary" | "transparent",
  className?: string,
  layout?: "default" | "remove",
  iconSize?: "xSmall" | "small" | "medium" | "large"
}

const AddToWishlist = (props: AddToWishlistPropsType) => {
  const countryLang = useCountryLang()
  const { productId, width, variant, className, layout } = props;
  const [inSelectedWishlist, setInSelectedWishlist] = useState(false);
  const {selectedWishlist, setSelectedWishlist} = useContext(SelectedWishlistContext);
  const [isLoading, setIsLoading] = useState(false)
  const queryClient = useQueryClient();
  const isUserLoggedIn = useUserLoggedIn();
  const {setLoggedOutWishlistCtx} = useContext(LoggedOutWishlistContext)
  const [sessionAtom] = useAtom(nextSessionAtom)
  const {session, status} = sessionAtom;

  const addToWishlistConfirmation = () => {
    toast("Added to wishlist", {
      type: "success"
    })
  }


  const addToSelectedWishlist = useMutation(async () => {
    const wishlistItem = {
      wishlistId: selectedWishlist?.id,
      productId: productId,
      quantity: 1
    }
    setIsLoading(true)
    const response = await request({url: `/${countryLang}/wishlist/position/add`, data: wishlistItem,
      method: "post", session: session})
    setIsLoading(false)
    addToWishlistConfirmation()
    setSelectedWishlist(response.data)
    await queryClient.invalidateQueries(["wishlists", countryLang])
    await queryClient.invalidateQueries(["wishlist", selectedWishlist?.id])
    return response
  })


  const removeFromSelectedWishlist = useMutation(async () => {
    setIsLoading(true)
    let wishlistId = props.wishlistId ? props.wishlistId : selectedWishlist?.id;
    const response = await request({url: `/${countryLang}/wishlist/position/delete?wishListId=${wishlistId}&productId=${productId}`,
      method: "delete", session: session})
    setIsLoading(false)
    setSelectedWishlist(response.data)
    await queryClient.cancelQueries(["wishlist", wishlistId, countryLang])
    await queryClient.invalidateQueries(["wishlist", wishlistId, countryLang])
    await queryClient.invalidateQueries(["wishlists", countryLang])
    return response
  })


  useEffect(() => {
    // Check if the product is in the selected wishlist and set the state
    if (selectedWishlist && selectedWishlist.products && selectedWishlist.products.length > 0) {
      selectedWishlist?.products.some(prod => prod.productId == productId) ? setInSelectedWishlist(true) : setInSelectedWishlist(false);
      setIsLoading(false)
    }
    else if (selectedWishlist && !(selectedWishlist.products && selectedWishlist.products.length > 0)) {
      // if the selected wishlist is empty
      setInSelectedWishlist(false)
      setIsLoading(false)
    }

    // logged-out user status check
    const localStorageWishlist = getLocalStorageItem("loggedOutWishlist")
    if (localStorageWishlist!=null) {
      const localWishlist = JSON.parse(localStorageWishlist);
      localWishlist.some((entry: ILoggedOutShoppingList) => entry.product.productID == productId) ? setInSelectedWishlist(true) : setInSelectedWishlist(false);
    }
  }, [selectedWishlist, setSelectedWishlist])


  const handleWishlistAction = async () => {
    // logged-out user
    if (!isUserLoggedIn) {
      if (layout=="remove" || inSelectedWishlist) {
        handleRemoveItemFromLoggedOutWishlist()
        setInSelectedWishlist(false)
      } else {
        await persistLoggedOutWishlist(productId, 1)
        setInSelectedWishlist(true)
      }
      // logged-in user
    } else {
      if (!inSelectedWishlist) {
        addToSelectedWishlist.mutate();
        setInSelectedWishlist(true)
      } else {
        removeFromSelectedWishlist.mutate();
        setInSelectedWishlist(false)
      }
    }
  }


  // If the user is not logged in we store the wishlist selection in localStorage
  // To be merged on login with account wishlist
  const persistLoggedOutWishlist = async (productId: number, quantity: number) => {
    setIsLoading(true)
    const res = await request({url: `/${countryLang}/product/${productId}`, method: 'get'})
    const product = res.data
    const entry = {
      product,
      quantity
    }
    const persistedWishlist = getLocalStorageItem("loggedOutWishlist");
    if (persistedWishlist!=null) {
      const wishlistProducts = [...JSON.parse(persistedWishlist)]
      if (!wishlistProducts.some((item: ILoggedOutShoppingList) => item.product.productID==entry.product.productID)) {
        wishlistProducts.push(entry)
        setLocalStorageItem("loggedOutWishlist", JSON.stringify(wishlistProducts))
        setLoggedOutWishlistCtx(wishlistProducts)
      }
    } else {
      setLocalStorageItem("loggedOutWishlist", JSON.stringify([entry]))
      setLoggedOutWishlistCtx([entry])
    }
    setIsLoading(false)
    addToWishlistConfirmation()
  }


  const handleRemoveItemFromLoggedOutWishlist = () => {
    setIsLoading(true)
    const persistedWishlist = getLocalStorageItem("loggedOutWishlist");
    if (persistedWishlist!=null) {
      const entries = JSON.parse(persistedWishlist);
      const filtered = entries.filter((item: ILoggedOutShoppingList) => item.product.productID!=productId);
      setLocalStorageItem("loggedOutWishlist", JSON.stringify(filtered))
      setLoggedOutWishlistCtx(filtered)
    }
    setIsLoading(false)
  }


  return (
    <>
      {(!isLoading && layout==="default") &&
        <Button variant={variant} width={width} className={className} onClick={handleWishlistAction}>
          {!inSelectedWishlist && <Icon variant={props.iconSize ? props.iconSize : "medium"} className={"d-inline-block"}>heart-line</Icon>}
          {inSelectedWishlist && <Icon variant={props.iconSize ? props.iconSize : "medium"} className={"d-inline-block"}>heart-fill</Icon>}
        </Button>
      }

      {(!isLoading && layout==="remove") &&
        <Button variant={variant} width={width} className={className} onClick={handleWishlistAction}>
          <Icon className={"d-inline-block"} variant="small">delete-bin</Icon>
        </Button>
      }

      {isLoading &&
        <Button variant={variant} width={width} className={className} disabled={true}>
          <Spinner animation="border" size="sm" />
        </Button>
      }

    </>
  )
}

export default AddToWishlist
